// src/utils/health/reportTemplates.ts
// Report Template Definitions for BrightPath Health Tracking

import type { ReportTemplate, ReportSection } from '@/features/health/types/healthTypes/reportTypes';

// ============================================================================
// Default Section Definitions
// ============================================================================

export const allSections: ReportSection[] = [
  {
    id: 'overview',
    name: 'Overview & Statistics',
    description: 'Summary statistics and key metrics for the reporting period',
    icon: 'BarChart3',
    enabled: true,
    order: 1,
  },
  {
    id: 'seizures',
    name: 'Seizure Activity',
    description: 'Detailed seizure log with duration, type, and triggers',
    icon: 'Zap',
    enabled: true,
    order: 2,
  },
  {
    id: 'medications',
    name: 'Medication Log',
    description: 'Medication administration records and compliance',
    icon: 'Pill',
    enabled: true,
    order: 3,
  },
  {
    id: 'symptoms',
    name: 'Symptom Tracking',
    description: 'Recorded symptoms with severity levels',
    icon: 'Thermometer',
    enabled: true,
    order: 4,
  },
  {
    id: 'therapy',
    name: 'Therapy Sessions',
    description: 'PT, OT, speech, and other therapy session records',
    icon: 'Heart',
    enabled: true,
    order: 5,
  },
  {
    id: 'vitals',
    name: 'Vital Signs',
    description: 'Temperature, heart rate, and blood pressure readings',
    icon: 'Activity',
    enabled: true,
    order: 6,
  },
  {
    id: 'sleep',
    name: 'Sleep Patterns',
    description: 'Sleep duration and quality tracking',
    icon: 'Moon',
    enabled: true,
    order: 7,
  },
  {
    id: 'feeding',
    name: 'Feeding & Nutrition',
    description: 'Feeding records and nutritional intake',
    icon: 'Utensils',
    enabled: true,
    order: 8,
  },
  {
    id: 'mood',
    name: 'Mood & Behavior',
    description: 'Mood tracking and behavioral observations',
    icon: 'Smile',
    enabled: true,
    order: 9,
  },
  {
    id: 'timeline',
    name: 'Event Timeline',
    description: 'Chronological view of all recorded events',
    icon: 'Clock',
    enabled: true,
    order: 10,
  },
];

// ============================================================================
// Report Templates
// ============================================================================

export const reportTemplates: ReportTemplate[] = [
  {
    id: 'comprehensive',
    name: 'Comprehensive Health Report',
    description: 'Complete overview of all health tracking data including seizures, medications, symptoms, therapy, and more.',
    type: 'comprehensive',
    icon: 'FileText',
    defaultSections: allSections.map(s => ({ ...s, enabled: true })),
    suggestedPeriod: 'month',
    forProviders: true,
    forLegal: true,
  },
  {
    id: 'seizure',
    name: 'Seizure Activity Report',
    description: 'Focused report on seizure events including frequency, duration, types, and identified triggers.',
    type: 'seizure',
    icon: 'Zap',
    defaultSections: [
      { ...allSections.find(s => s.id === 'overview')!, enabled: true },
      { ...allSections.find(s => s.id === 'seizures')!, enabled: true },
      { ...allSections.find(s => s.id === 'medications')!, enabled: true },
      { ...allSections.find(s => s.id === 'sleep')!, enabled: true },
      { ...allSections.find(s => s.id === 'timeline')!, enabled: true },
    ],
    suggestedPeriod: 'month',
    forProviders: true,
    forLegal: true,
  },
  {
    id: 'medication',
    name: 'Medication Compliance Report',
    description: 'Track medication administration, compliance rates, and any noted side effects.',
    type: 'medication',
    icon: 'Pill',
    defaultSections: [
      { ...allSections.find(s => s.id === 'overview')!, enabled: true },
      { ...allSections.find(s => s.id === 'medications')!, enabled: true },
      { ...allSections.find(s => s.id === 'symptoms')!, enabled: true },
      { ...allSections.find(s => s.id === 'timeline')!, enabled: true },
    ],
    suggestedPeriod: 'month',
    forProviders: true,
    forLegal: false,
  },
  {
    id: 'therapy',
    name: 'Therapy Progress Report',
    description: 'Summary of therapy sessions including PT, OT, speech therapy, and documented progress.',
    type: 'therapy',
    icon: 'Heart',
    defaultSections: [
      { ...allSections.find(s => s.id === 'overview')!, enabled: true },
      { ...allSections.find(s => s.id === 'therapy')!, enabled: true },
      { ...allSections.find(s => s.id === 'timeline')!, enabled: true },
    ],
    suggestedPeriod: 'quarter',
    forProviders: true,
    forLegal: true,
  },
  {
    id: 'symptom',
    name: 'Symptom Tracking Report',
    description: 'Detailed symptom log with severity trends and pattern analysis.',
    type: 'symptom',
    icon: 'Thermometer',
    defaultSections: [
      { ...allSections.find(s => s.id === 'overview')!, enabled: true },
      { ...allSections.find(s => s.id === 'symptoms')!, enabled: true },
      { ...allSections.find(s => s.id === 'vitals')!, enabled: true },
      { ...allSections.find(s => s.id === 'mood')!, enabled: true },
      { ...allSections.find(s => s.id === 'timeline')!, enabled: true },
    ],
    suggestedPeriod: 'week',
    forProviders: true,
    forLegal: false,
  },
  {
    id: 'vitals',
    name: 'Vital Signs Report',
    description: 'Vital sign trends including temperature, heart rate, and blood pressure over time.',
    type: 'vitals',
    icon: 'Activity',
    defaultSections: [
      { ...allSections.find(s => s.id === 'overview')!, enabled: true },
      { ...allSections.find(s => s.id === 'vitals')!, enabled: true },
      { ...allSections.find(s => s.id === 'timeline')!, enabled: true },
    ],
    suggestedPeriod: 'week',
    forProviders: true,
    forLegal: false,
  },
  {
    id: 'custom',
    name: 'Custom Report',
    description: 'Build your own report by selecting the sections and date range you need.',
    type: 'custom',
    icon: 'Settings',
    defaultSections: allSections.map(s => ({ ...s, enabled: false })),
    suggestedPeriod: 'month',
    forProviders: true,
    forLegal: true,
  },
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get a template by its ID or type
 */
export function getTemplateById(id: string): ReportTemplate | undefined {
  return reportTemplates.find(t => t.id === id || t.type === id);
}

/**
 * Get templates suitable for providers
 */
export function getProviderTemplates(): ReportTemplate[] {
  return reportTemplates.filter(t => t.forProviders);
}

/**
 * Get templates suitable for legal cases
 */
export function getLegalTemplates(): ReportTemplate[] {
  return reportTemplates.filter(t => t.forLegal);
}

/**
 * Get section by ID
 */
export function getSectionById(id: string): ReportSection | undefined {
  return allSections.find(s => s.id === id);
}

/**
 * Create custom sections configuration
 */
export function createCustomSections(enabledIds: string[]): ReportSection[] {
  return allSections.map(section => ({
    ...section,
    enabled: enabledIds.includes(section.id),
  }));
}

/**
 * Get suggested date range for a period
 */
export function getSuggestedDateRange(period: string): { startDate: Date; endDate: Date } {
  const endDate = new Date();
  const startDate = new Date();

  switch (period) {
    case 'week':
      startDate.setDate(startDate.getDate() - 7);
      break;
    case 'month':
      startDate.setMonth(startDate.getMonth() - 1);
      break;
    case 'quarter':
      startDate.setMonth(startDate.getMonth() - 3);
      break;
    case 'year':
      startDate.setFullYear(startDate.getFullYear() - 1);
      break;
    default:
      startDate.setMonth(startDate.getMonth() - 1);
  }

  return { startDate, endDate };
}

export default reportTemplates;

